<script setup>
import { computed, ref, reactive, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import UploadApi from 'dashboard/api/upload';

const store = useStore();
const getters = useStoreGetters();

const autoReplies = computed(
  () => getters['notificationAutoReplies/getAutoReplies'].value
);
const uiFlags = computed(
  () => getters['notificationAutoReplies/getUIFlags'].value
);

const whatsAppInboxes = computed(() => {
  const allInboxes = getters['inboxes/getInboxes'].value || [];
  return allInboxes.filter(
    i =>
      i.channel_type === 'Channel::Whatsapp' ||
      i.channel_type === 'Channel::Api'
  );
});

const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const showDialog = ref(false);
const editingReply = ref(null);

const form = reactive({
  inboxId: '',
  sourceTemplateName: '',
  sourceTemplateLanguage: 'pt_BR',
  buttonPayload: '',
  replyType: 'template',
  replyTemplateName: '',
  replyTemplateLanguage: 'pt_BR',
  replyText: '',
  replyImageUrl: '',
  replyAttachmentUrl: '',
});

onMounted(() => {
  store.dispatch('notificationAutoReplies/get');
  store.dispatch('inboxes/get');
});

const sourceTemplateOptions = computed(() => {
  if (!form.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates.value(form.inboxId) || [];
  return templates.filter(t => {
    const buttons = t.components?.find(c => c.type === 'BUTTONS');
    return buttons?.buttons?.length > 0;
  });
});

const selectedSourceTemplate = computed(() => {
  return sourceTemplateOptions.value.find(
    t => t.name === form.sourceTemplateName
  );
});

const buttonOptions = computed(() => {
  if (!selectedSourceTemplate.value) return [];
  const buttons =
    selectedSourceTemplate.value.components?.find(c => c.type === 'BUTTONS')
      ?.buttons || [];
  return buttons.map(b => ({ text: b.text, type: b.type }));
});

const replyTemplateOptions = computed(() => {
  if (!form.inboxId) return [];
  return getFilteredWhatsAppTemplates.value(form.inboxId) || [];
});

watch(
  () => form.inboxId,
  () => {
    form.sourceTemplateName = '';
    form.buttonPayload = '';
    form.replyTemplateName = '';
  }
);

watch(
  () => form.sourceTemplateName,
  () => {
    form.buttonPayload = '';
    const tmpl = selectedSourceTemplate.value;
    if (tmpl) {
      form.sourceTemplateLanguage = tmpl.language || 'pt_BR';
    }
  }
);

const handleNew = () => {
  editingReply.value = null;
  Object.assign(form, {
    inboxId: '',
    sourceTemplateName: '',
    sourceTemplateLanguage: 'pt_BR',
    buttonPayload: '',
    replyType: 'template',
    replyTemplateName: '',
    replyTemplateLanguage: 'pt_BR',
    replyText: '',
    replyImageUrl: '',
    replyAttachmentUrl: '',
  });
  showDialog.value = true;
};

const handleEdit = reply => {
  editingReply.value = reply;
  Object.assign(form, {
    inboxId: reply.inbox_id,
    sourceTemplateName: reply.source_template_name,
    sourceTemplateLanguage: reply.source_template_language,
    buttonPayload: reply.button_payload,
    replyType: reply.reply_type || 'template',
    replyTemplateName: reply.reply_template_name || '',
    replyTemplateLanguage: reply.reply_template_language || 'pt_BR',
    replyText: reply.reply_text || '',
    replyImageUrl: reply.reply_image_url || '',
    replyAttachmentUrl: reply.reply_attachment_url || '',
  });
  showDialog.value = true;
};

const handleCloseDialog = () => {
  showDialog.value = false;
  editingReply.value = null;
};

const isFormValid = computed(() => {
  const base = form.inboxId && form.sourceTemplateName && form.buttonPayload;
  if (form.replyType === 'text') return base && form.replyText.trim();
  return base && form.replyTemplateName;
});

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  const data = {
    inbox_id: form.inboxId,
    source_template_name: form.sourceTemplateName,
    source_template_language: form.sourceTemplateLanguage,
    button_payload: form.buttonPayload,
    reply_type: form.replyType,
  };

  if (form.replyType === 'template') {
    const replyTmpl = replyTemplateOptions.value.find(
      t => t.name === form.replyTemplateName
    );
    data.reply_template_name = form.replyTemplateName;
    data.reply_template_language =
      replyTmpl?.language || form.replyTemplateLanguage;
    data.reply_text = null;
    data.reply_image_url = null;
    data.reply_attachment_url = null;
  } else {
    data.reply_text = form.replyText;
    data.reply_image_url = form.replyImageUrl || null;
    data.reply_attachment_url = form.replyAttachmentUrl || null;
    data.reply_template_name = null;
    data.reply_template_language = null;
  }

  try {
    if (editingReply.value) {
      await store.dispatch('notificationAutoReplies/update', {
        id: editingReply.value.id,
        ...data,
      });
      useAlert('Auto-resposta atualizada com sucesso');
    } else {
      await store.dispatch('notificationAutoReplies/create', data);
      useAlert('Auto-resposta criada com sucesso');
    }
    handleCloseDialog();
  } catch {
    useAlert('Erro ao salvar auto-resposta');
  }
};

const handleToggle = async reply => {
  try {
    await store.dispatch('notificationAutoReplies/toggle', reply.id);
    useAlert(reply.enabled ? 'Auto-resposta desativada' : 'Auto-resposta ativada');
  } catch {
    useAlert('Erro ao alternar auto-resposta');
  }
};

const handleDelete = async reply => {
  try {
    await store.dispatch('notificationAutoReplies/delete', reply.id);
    useAlert('Auto-resposta excluída com sucesso');
  } catch {
    useAlert('Erro ao excluir auto-resposta');
  }
};

const getInboxName = inboxId => {
  const inbox = whatsAppInboxes.value.find(i => i.id === inboxId);
  return inbox?.name || '-';
};

const imageInputRef = ref(null);
const attachmentInputRef = ref(null);
const isUploadingImage = ref(false);
const isUploadingAttachment = ref(false);

const handleImageUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingImage.value = true;
  try {
    const response = await UploadApi.uploadFile(file);
    form.replyImageUrl = response.data?.file_url || '';
    useAlert('Imagem enviada com sucesso');
  } catch {
    useAlert('Erro ao enviar imagem');
  } finally {
    isUploadingImage.value = false;
    if (imageInputRef.value) imageInputRef.value.value = '';
  }
};

const handleAttachmentUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingAttachment.value = true;
  try {
    const response = await UploadApi.uploadFile(file);
    form.replyAttachmentUrl = response.data?.file_url || '';
    useAlert('Anexo enviado com sucesso');
  } catch {
    useAlert('Erro ao enviar anexo');
  } finally {
    isUploadingAttachment.value = false;
    if (attachmentInputRef.value) attachmentInputRef.value.value = '';
  }
};

const formatTemplateName = name => {
  return (name || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12">
        Auto Respostas de Botões
      </h1>
      <button
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
        @click="handleNew"
      >
        Nova Auto Resposta
      </button>
    </div>

    <p class="mb-4 text-sm text-n-slate-11">
      Configure respostas automáticas para quando o destinatário clicar em um
      botão do template WhatsApp. O sistema enviará automaticamente um template
      ou uma mensagem de texto como resposta.
    </p>

    <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-8">
      <span class="text-n-slate-11">Carregando...</span>
    </div>

    <div v-else-if="autoReplies.length === 0" class="flex flex-col items-center justify-center py-16">
      <div class="i-lucide-reply w-12 h-12 text-n-slate-9 mb-4" />
      <p class="text-n-slate-11">
        Nenhuma auto-resposta configurada
      </p>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="text-xs uppercase bg-n-alpha-2 text-n-slate-11">
          <tr>
            <th class="px-4 py-3">Inbox</th>
            <th class="px-4 py-3">Template Fonte</th>
            <th class="px-4 py-3">Botão</th>
            <th class="px-4 py-3">Resposta</th>
            <th class="px-4 py-3">Status</th>
            <th class="px-4 py-3">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="reply in autoReplies"
            :key="reply.id"
            class="border-b border-n-strong"
          >
            <td class="px-4 py-3 text-n-slate-11">
              {{ getInboxName(reply.inbox_id) }}
            </td>
            <td class="px-4 py-3 font-medium text-n-slate-12">
              {{ formatTemplateName(reply.source_template_name) }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              <span class="px-2 py-1 text-xs rounded-full bg-n-alpha-2">
                {{ reply.button_payload }}
              </span>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              <template v-if="reply.reply_type === 'text'">
                <span class="px-2 py-0.5 text-xs rounded bg-n-alpha-2 text-n-blue-11 mr-1">Texto</span>
                {{ (reply.reply_text || '').substring(0, 50) }}{{ (reply.reply_text || '').length > 50 ? '...' : '' }}
              </template>
              <template v-else>
                {{ formatTemplateName(reply.reply_template_name) }}
              </template>
            </td>
            <td class="px-4 py-3">
              <button
                :class="reply.enabled ? 'text-n-teal-11' : 'text-n-slate-9'"
                class="text-xs font-medium"
                @click="handleToggle(reply)"
              >
                {{ reply.enabled ? 'Ativo' : 'Inativo' }}
              </button>
            </td>
            <td class="px-4 py-3">
              <div class="flex gap-2">
                <button
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11"
                  @click="handleEdit(reply)"
                >
                  Editar
                </button>
                <button
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-ruby-11"
                  @click="handleDelete(reply)"
                >
                  Excluir
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Dialog -->
    <div
      v-if="showDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black5"
      @click.self="handleCloseDialog"
    >
      <div
        class="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-n-surface-1 rounded-xl shadow-xl p-6"
      >
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-n-slate-12">
            {{
              editingReply ? 'Editar Auto Resposta' : 'Nova Auto Resposta'
            }}
          </h2>
          <button
            class="p-1 rounded hover:bg-n-alpha-2"
            @click="handleCloseDialog"
          >
            <div class="i-lucide-x w-5 h-5 text-n-slate-11" />
          </button>
        </div>

        <div class="flex flex-col gap-4">
          <!-- Inbox -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              Inbox WhatsApp *
            </label>
            <select
              v-model="form.inboxId"
              class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            >
              <option value="">Selecione</option>
              <option
                v-for="inbox in whatsAppInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>

          <!-- Source Template -->
          <div v-if="form.inboxId" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              Template com Botões *
            </label>
            <select
              v-model="form.sourceTemplateName"
              class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            >
              <option value="">Selecione o template</option>
              <option
                v-for="tmpl in sourceTemplateOptions"
                :key="tmpl.name"
                :value="tmpl.name"
              >
                {{ formatTemplateName(tmpl.name) }} ({{ tmpl.language }})
              </option>
            </select>
          </div>

          <!-- Button -->
          <div v-if="buttonOptions.length > 0" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              Botão que dispara a resposta *
            </label>
            <select
              v-model="form.buttonPayload"
              class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            >
              <option value="">Selecione o botão</option>
              <option
                v-for="btn in buttonOptions"
                :key="btn.text"
                :value="btn.text"
              >
                {{ btn.text }} ({{ btn.type }})
              </option>
            </select>
          </div>

          <!-- Reply Type -->
          <div v-if="form.buttonPayload" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              Tipo de Resposta *
            </label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input
                  v-model="form.replyType"
                  type="radio"
                  value="template"
                  class="accent-n-brand"
                >
                Template WhatsApp
              </label>
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input
                  v-model="form.replyType"
                  type="radio"
                  value="text"
                  class="accent-n-brand"
                >
                Mensagem de Texto
              </label>
            </div>
          </div>

          <!-- Reply Template -->
          <div v-if="form.inboxId && form.replyType === 'template'" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              Template de Resposta *
            </label>
            <select
              v-model="form.replyTemplateName"
              class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            >
              <option value="">Selecione o template de resposta</option>
              <option
                v-for="tmpl in replyTemplateOptions"
                :key="tmpl.name"
                :value="tmpl.name"
              >
                {{ formatTemplateName(tmpl.name) }} ({{ tmpl.language }})
              </option>
            </select>
          </div>

          <!-- Reply Text -->
          <div v-if="form.replyType === 'text'" class="flex flex-col gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Mensagem de Texto *
              </label>
              <textarea
                v-model="form.replyText"
                rows="4"
                placeholder="Digite a mensagem. Use variáveis: {{nome}}, {{cumprimento}}, {{empresa}}, {{email}}, {{phone}}"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12 resize-y"
              />
              <p
                class="text-xs text-n-slate-10"
                v-text="'Variáveis: {{cumprimento}}, {{nome}}, {{name}}, {{first_name}}, {{email}}, {{phone}}, {{empresa}}'"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Imagem (opcional)
              </label>
              <div class="flex items-center gap-2">
                <input
                  v-model="form.replyImageUrl"
                  type="url"
                  placeholder="URL da imagem ou faça upload"
                  class="flex-1 px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
                />
                <button
                  type="button"
                  :disabled="isUploadingImage"
                  class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                  @click="imageInputRef?.click()"
                >
                  <span
                    v-if="isUploadingImage"
                    class="i-lucide-loader-2 w-4 h-4 animate-spin"
                  />
                  <span v-else class="i-lucide-upload w-4 h-4" />
                  {{ isUploadingImage ? 'Enviando...' : 'Upload' }}
                </button>
                <input
                  ref="imageInputRef"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  class="hidden"
                  @change="handleImageUpload"
                />
              </div>
              <div
                v-if="form.replyImageUrl"
                class="mt-1 flex items-center gap-2"
              >
                <img
                  :src="form.replyImageUrl"
                  class="w-16 h-16 object-cover rounded border border-n-strong"
                />
                <button
                  type="button"
                  class="text-xs text-n-ruby-11 hover:underline"
                  @click="form.replyImageUrl = ''"
                >
                  Remover
                </button>
              </div>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Anexo (opcional)
              </label>
              <div class="flex items-center gap-2">
                <input
                  v-model="form.replyAttachmentUrl"
                  type="url"
                  placeholder="URL do arquivo ou faça upload"
                  class="flex-1 px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
                />
                <button
                  type="button"
                  :disabled="isUploadingAttachment"
                  class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                  @click="attachmentInputRef?.click()"
                >
                  <span
                    v-if="isUploadingAttachment"
                    class="i-lucide-loader-2 w-4 h-4 animate-spin"
                  />
                  <span v-else class="i-lucide-upload w-4 h-4" />
                  {{ isUploadingAttachment ? 'Enviando...' : 'Upload' }}
                </button>
                <input
                  ref="attachmentInputRef"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                  class="hidden"
                  @change="handleAttachmentUpload"
                />
              </div>
              <p
                v-if="form.replyAttachmentUrl"
                class="mt-1 flex items-center gap-2 text-xs text-n-teal-11"
              >
                <span class="i-lucide-paperclip w-3 h-3" />
                Anexo carregado
                <button
                  type="button"
                  class="text-n-ruby-11 hover:underline"
                  @click="form.replyAttachmentUrl = ''"
                >
                  Remover
                </button>
              </p>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 mt-6">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
            @click="handleCloseDialog"
          >
            Cancelar
          </button>
          <button
            :disabled="!isFormValid"
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
            @click="handleSubmit"
          >
            {{ editingReply ? 'Salvar' : 'Criar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
